/*
 * Soulog highlights gallery (sections/soulog-highlights.liquid, styles in assets/soulog-highlights.css)
 *
 * Modeled on "Get the highlights." at apple.com/iphone-18-pro:
 *   · A horizontal row of cards, the current card centered with equally wide neighbors peeking on both sides, native overflow-x + scroll-snap (touch / trackpad / keyboard work out of the box);
 *     this only adds "when to play, how long to play, where to go after";
 *   · Pacing is set by **the current card's own duration**: a video advances to the next card only when it ends (ended); image cards stay for data-image-ms;
 *   · The current dot stretches into a pill whose progress bar follows the video's currentTime / the image timer (writes --hl-progress every frame);
 *   · After the last card it stops and the button becomes "Replay" — it doesn't automatically rewind to the first card and loop (same as Apple).
 *
 * There are only two states:
 *   wantsPlay  User intent (what the play / pause button toggles). Initial value = autoplay enabled in the theme editor and "reduce motion" not enabled.
 *   finished   The last card has finished. The button shows "Replay"; clicking again starts from the first card.
 * Actually running = wantsPlay && section in viewport && tab visible && no card selected in the editor — see isRunning().
 * If any condition fails, the video is paused and the image timer frozen in place; on resume it continues from where it stopped.
 *
 * Loading: every <video> is preload="none". Until the section is "near" the current card is capped at metadata; once near, the current card is raised
 * to auto and the next one to metadata. preload only ever goes up. "Near" = first pixel visible (the rootMargin-0 observer), or — after the visitor
 * has scrolled — within LOOKAHEAD_SCREENS viewports of the fold, so the bytes are ready by the time it shows.
 * (2026-09-27: raising to auto in connectedCallback made mobile visitors download 2.7MB on page load without scrolling —
 * the section sits right under the hero, so any look-ahead margin fires at load; hence the gate is "first pixel visible".)
 * Posters: rendered in the markup as a srcset <img> underneath each video (not set here — waiting for this deferred script left first-visit cards blank for seconds, 2026-09-28).
 * On mobile, source swapping is done beforehand by the global soulog-mobile-video-src.js.
 *
 * The pure logic (nearest snap point / progress / next step) lives in static methods on the class, called directly by unit tests, see
 * tests/unit/soulog-highlights.test.mjs.
 */
if (!customElements.get("soulog-highlights")) {
	// Fallback and floor for image card duration: any shorter and it switches away before you can see it
	const DEFAULT_IMAGE_MS = 5000;
	const MIN_IMAGE_MS = 1000;
	// How long without a new scroll event after a manual swipe counts as "stopped" (fallback when scrollend isn't supported)
	const SETTLE_MS = 120;
	// After the visitor scrolls, start the full download once the gallery is within this many viewports of the fold
	const LOOKAHEAD_SCREENS = 1.5;

	class SoulogHighlights extends HTMLElement {
		/**
		 * Each card's snap point (scrollLeft) = offsetLeft - first card's offsetLeft.
		 * Track left/right padding = (section width - card width) / 2 (see --shl-side in the css), so at this position the card is exactly centered,
		 * the first / last cards included. Capping at the max scroll distance is just a fallback (sub-pixel error / layout not yet settled).
		 */
		static stopsOf(lefts, maxScroll) {
			const first = lefts.length ? lefts[0] : 0;
			return lefts.map((l) => Math.min(Math.max(0, l - first), Math.max(0, maxScroll)));
		}

		/** Index of the card whose snap point is closest to the current scrollLeft (ties go to the earlier one) */
		static nearestIndex(stops, scrollLeft) {
			let best = 0;
			let bestDist = Infinity;
			stops.forEach((c, i) => {
				const d = Math.abs(c - scrollLeft);
				if (d < bestDist) {
					bestDist = d;
					best = i;
				}
			});
			return best;
		}

		/**
		 * Progress of the current card, 0~1.
		 * Video: currentTime / duration; when the duration is unknown yet (no metadata / streaming Infinity) it counts as 0,
		 * so the progress bar doesn't flash full the moment it loads. Image: time elapsed / dwell duration.
		 */
		static progressOf({ isVideo, currentTime, duration, elapsed, imageMs }) {
			const raw = isVideo
				? Number.isFinite(duration) && duration > 0
					? currentTime / duration
					: 0
				: imageMs > 0
					? elapsed / imageMs
					: 1;
			return Math.min(1, Math.max(0, raw || 0));
		}

		/** After the current card finishes: go to the next one if there is one, otherwise stop here and mark "finished" */
		static nextStep(index, count) {
			return index < count - 1 ? { index: index + 1, finished: false } : { index, finished: true };
		}

		/** data-image-ms that is missing / invalid / too short all fall back to 5 seconds */
		static imageDuration(raw) {
			const ms = Number(raw);
			return Number.isFinite(ms) && ms >= MIN_IMAGE_MS ? ms : DEFAULT_IMAGE_MS;
		}

		connectedCallback() {
			this.track = this.querySelector("[data-hl-track]");
			if (!this.track) return;
			this.dots = Array.from(this.querySelectorAll("[data-hl-dot]"));
			this.toggle = this.querySelector("[data-hl-toggle]");
			// Mobile (<768) swaps the 8px dots for prev / next arrows — the dots were too small to hit reliably (2026-09-28)
			this.prevBtn = this.querySelector("[data-hl-prev]");
			this.nextBtn = this.querySelector("[data-hl-next]");
			this.imageMs = SoulogHighlights.imageDuration(this.dataset.imageMs);
			this.reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

			this.index = 0;
			this.elapsed = 0; // ms the image card has been shown (frozen while paused)
			this.lastTick = 0;
			this.finished = false;
			this.inView = false;
			this.near = false; // has the section shown its first pixel? Before that the current card is capped at metadata
			this.editorHold = false;
			this.programmatic = false; // during a smooth scroll started by goTo, don't change the current card based on scroll position
			this.stuck = new WeakSet(); // videos that won't play: treat them with the image timer so nothing gets stuck
			this.wantsPlay = this.hasAttribute("data-autoplay") && !this.reducedMotion.matches;

			this.onScroll = this.onScroll.bind(this);
			this.onSettle = this.onSettle.bind(this);
			this.onVisibility = () => this.update();
			this.tick = this.tick.bind(this);

			this.track.addEventListener("scroll", this.onScroll, { passive: true });
			this.track.addEventListener("scrollend", this.onSettle);
			document.addEventListener("visibilitychange", this.onVisibility);

			this.dots.forEach((dot, i) =>
				dot.addEventListener("click", () => {
					this.finished = false;
					this.goTo(i);
				}),
			);
			if (this.toggle) this.toggle.addEventListener("click", () => this.onToggle());
			if (this.prevBtn) this.prevBtn.addEventListener("click", () => this.step(-1));
			if (this.nextBtn) this.nextBtn.addEventListener("click", () => this.step(1));

			this.cards().forEach((card) => {
				const video = videoIn(card);
				if (!video) return;
				video.addEventListener("ended", () => {
					if (this.cards()[this.index] === card) this.complete();
				});
				video.addEventListener("error", () => this.markStuck(video));
			});

			// Start as soon as the gallery shows its first pixel. No threshold — on wide viewports a tall stage's visible ratio may never reach it.
			// (2026-09-28: this used to shrink the viewport by 15% on top of "download only on first pixel", so the video only started once the card
			// was almost fully on screen. Now the look-ahead below has the bytes ready by then, and playback starts right away.)
			this.io = new IntersectionObserver(
				(entries) => {
					this.inView = entries[entries.length - 1].isIntersecting;
					if (this.inView) this.approach();
					// The control bar's float-in / collapse animation hangs off this attribute (CSS in the "control bar" part)
					this.toggleAttribute("data-hl-in-view", this.inView);
					this.update();
				},
				{ rootMargin: "0px" },
			);
			// Only hide the control bar pending the animation once JS has taken over — if the script fails, the control bar shows as usual
			this.setAttribute("data-hl-ready", "");
			this.io.observe(this);
			this.nearIo = new IntersectionObserver((entries) => {
				if (entries.some((e) => e.isIntersecting)) this.approach();
			});
			this.nearIo.observe(this);
			/*
			 * Look-ahead download: once the visitor has actually scrolled and the gallery is within LOOKAHEAD_SCREENS viewports of the fold,
			 * start downloading before it shows, so it can play the moment it appears. Tied to a real scroll event rather than an IO rootMargin:
			 * the section sits right under the hero, so any margin would fire at page load (the 2.7MB-without-scrolling problem, 2026-09-27).
			 */
			this.onLookahead = () => {
				if (this.getBoundingClientRect().top < window.innerHeight * LOOKAHEAD_SCREENS) this.approach();
			};
			window.addEventListener("scroll", this.onLookahead, { passive: true });

			// Theme editor: selecting a card scrolls to it and holds; deselecting resumes
			this.onBlockSelect = (e) => {
				const i = this.cards().indexOf(e.target.closest ? e.target.closest("[data-hl-card]") : null);
				if (i < 0) return;
				this.editorHold = true;
				this.goTo(i, true);
			};
			this.onBlockDeselect = () => {
				this.editorHold = false;
				this.update();
			};
			document.addEventListener("shopify:block:select", this.onBlockSelect);
			document.addEventListener("shopify:block:deselect", this.onBlockDeselect);

			this.activate(0);
		}

		disconnectedCallback() {
			if (!this.track) return;
			cancelAnimationFrame(this.raf);
			clearTimeout(this.settleTimer);
			if (this.io) this.io.disconnect();
			if (this.nearIo) this.nearIo.disconnect();
			window.removeEventListener("scroll", this.onLookahead);
			document.removeEventListener("visibilitychange", this.onVisibility);
			document.removeEventListener("shopify:block:select", this.onBlockSelect);
			document.removeEventListener("shopify:block:deselect", this.onBlockDeselect);
			// Editor moving / deleting the section: don't let the video keep decoding in the background
			this.cards().forEach((card) => {
				const video = videoIn(card);
				if (video && !video.paused) video.pause();
			});
		}

		cards() {
			return Array.from(this.track.querySelectorAll("[data-hl-card]"));
		}

		isRunning() {
			return this.wantsPlay && !this.finished && this.inView && !document.hidden && !this.editorHold;
		}

		/* ---------------------------------------------------------------- Switching */

		stops() {
			const track = this.track;
			return SoulogHighlights.stopsOf(
				this.cards().map((c) => c.offsetLeft),
				track.scrollWidth - track.clientWidth,
			);
		}

		/** Scroll to card i (centered) and make it the current card */
		goTo(i, instant) {
			const left = this.stops()[i];
			if (left === undefined) return;
			this.programmatic = true;
			clearTimeout(this.settleTimer);
			this.settleTimer = setTimeout(this.onSettle, 900); // fallback when scrollend never arrives
			this.track.scrollTo({
				left,
				behavior: instant || this.reducedMotion.matches ? "auto" : "smooth",
			});
			this.activate(i);
		}

		/** Arrow buttons: move one card back / forward, stopping at either end (no wrap-around, same as autoplay) */
		step(delta) {
			const i = this.index + delta;
			if (i < 0 || i >= this.cards().length) return;
			this.finished = false;
			this.goTo(i);
		}

		/** Make card i the current card: reset progress to zero, stop and rewind videos on other cards, preload the next one */
		activate(i) {
			const cards = this.cards();
			this.index = i;
			this.elapsed = 0;
			this.lastTick = 0;

			cards.forEach((card, n) => {
				const video = videoIn(card);
				card.toggleAttribute("data-active", n === i);
				if (!video) return;
				if (n === i) rewind(video);
				else if (!video.paused) video.pause();
			});
			this.preloadAround();

			this.dots.forEach((dot, n) => {
				dot.setAttribute("aria-current", n === i ? "true" : "false");
				dot.style.setProperty("--hl-progress", "0");
			});
			if (this.prevBtn) this.prevBtn.disabled = i <= 0;
			if (this.nextBtn) this.nextBtn.disabled = i >= cards.length - 1;
			this.update();
		}

		/** The section showed its first pixel: from now on the current card downloads in full */
		approach() {
			if (this.near) return;
			this.near = true;
			if (this.nearIo) this.nearIo.disconnect();
			window.removeEventListener("scroll", this.onLookahead);
			this.preloadAround();
		}

		/** Current card: auto once near, metadata before; next card: metadata once near; everything else untouched */
		preloadAround() {
			this.cards().forEach((card, n) => {
				const video = videoIn(card);
				if (!video) return;
				if (n === this.index) raisePreload(video, this.near ? "auto" : "metadata");
				else if (n === this.index + 1 && this.near) raisePreload(video, "metadata");
			});
		}

		/** The current card has finished */
		complete() {
			const step = SoulogHighlights.nextStep(this.index, this.cards().length);
			if (step.finished) {
				this.finished = true;
				this.setProgress(1);
				this.update();
				return;
			}
			this.goTo(step.index);
		}

		onToggle() {
			if (this.finished) {
				this.finished = false;
				this.wantsPlay = true;
				this.goTo(0);
				return;
			}
			this.wantsPlay = !this.wantsPlay;
			this.update();
		}

		/* ---------------------------------------------------------------- Manual swipe */

		onScroll() {
			if (this.programmatic) return;
			// Move the pill to the nearest card's dot while dragging (2026-09-28: it used to wait for scrollend, lagging behind the finger);
			// switching the card / video itself still waits for the swipe to settle
			this.markDot(SoulogHighlights.nearestIndex(this.stops(), this.track.scrollLeft));
			clearTimeout(this.settleTimer);
			this.settleTimer = setTimeout(this.onSettle, SETTLE_MS);
		}

		/** Only the dots' "current" look; the playing card stays this.index until onSettle */
		markDot(i) {
			this.dots.forEach((dot, n) => {
				const current = n === i ? "true" : "false";
				if (dot.getAttribute("aria-current") !== current) dot.setAttribute("aria-current", current);
			});
		}

		/** Swipe stopped: if it stopped on another card, make that card current (play from the start) */
		onSettle() {
			clearTimeout(this.settleTimer);
			if (this.programmatic) {
				this.programmatic = false;
				return;
			}
			const i = SoulogHighlights.nearestIndex(this.stops(), this.track.scrollLeft);
			if (i !== this.index) {
				this.finished = false;
				this.activate(i);
			} else {
				this.markDot(i); // dragged away and back: return the pill to the playing card
			}
		}

		/* ---------------------------------------------------------------- Playback */

		/** Call once after any state change: bring video / timer / button in line with isRunning() */
		update() {
			const running = this.isRunning();
			const video = videoIn(this.cards()[this.index]);

			if (video && !this.stuck.has(video)) {
				if (running && video.paused && !video.ended) {
					const played = video.play && video.play();
					// muted + playsinline is rarely rejected; if it really is, treat it as unplayable and advance on the image timer
					if (played && typeof played.catch === "function") played.catch(() => this.markStuck(video));
				} else if (!running && !video.paused) {
					video.pause();
				}
			}

			cancelAnimationFrame(this.raf);
			this.lastTick = 0;
			if (running) this.raf = requestAnimationFrame(this.tick);

			this.syncToggle();
		}

		tick(now) {
			if (!this.isRunning()) return;
			const video = videoIn(this.cards()[this.index]);
			const timed = !video || this.stuck.has(video);

			if (timed) {
				if (this.lastTick) this.elapsed += now - this.lastTick;
				this.lastTick = now;
			}

			const p = SoulogHighlights.progressOf({
				isVideo: !timed,
				currentTime: video ? video.currentTime : 0,
				duration: video ? video.duration : 0,
				elapsed: this.elapsed,
				imageMs: this.imageMs,
			});
			this.setProgress(p);

			// A video's "finished" is left to the ended event (progress reaching 1 doesn't mean it has ended); image cards wrap up here
			if (timed && p >= 1) {
				this.complete();
				return;
			}
			this.raf = requestAnimationFrame(this.tick);
		}

		markStuck(video) {
			if (this.stuck.has(video)) return;
			this.stuck.add(video);
			this.update();
		}

		setProgress(p) {
			const dot = this.dots[this.index];
			if (dot) dot.style.setProperty("--hl-progress", String(p));
		}

		syncToggle() {
			if (!this.toggle) return;
			const state = this.finished ? "finished" : this.wantsPlay ? "playing" : "paused";
			this.toggle.dataset.state = state;
			const label =
				state === "finished"
					? this.toggle.dataset.labelReplay
					: state === "playing"
						? this.toggle.dataset.labelPause
						: this.toggle.dataset.labelPlay;
			if (label) this.toggle.setAttribute("aria-label", label);
		}
	}

	/** The video inside a card (null if none). The card may be replaced by the editor, so look it up each time instead of caching */
	function videoIn(card) {
		return card && card.querySelector ? card.querySelector("[data-hl-video]") : null;
	}

	const PRELOAD_RANK = { none: 0, metadata: 1, auto: 2 };
	/** preload only goes up: never pull a card that is already downloading back to metadata */
	function raisePreload(video, want) {
		if ((PRELOAD_RANK[video.preload] || 0) < PRELOAD_RANK[want]) video.preload = want;
	}

	/** Back to the start: a card whose turn comes again plays from the beginning. Skip directly when there's no seekable timeline yet; the following play starts from the beginning anyway */
	function rewind(video) {
		if (!video.currentTime) return;
		try {
			video.currentTime = 0;
		} catch (_) {
			/* noop */
		}
	}

	customElements.define("soulog-highlights", SoulogHighlights);
}
